// Per page SEO metadata for the SPA. React swaps views client side, so the
// document head must be updated imperatively on every route change. Google
// executes JavaScript and indexes what this writes; the static defaults in
// public/index.html cover first paint and social link previews.
//
// Brand rule reminder: no dashes in user facing copy, use "|" in titles.
import { useEffect } from "react";

export const SITE_URL = "https://swiprent.com";
const SITE_NAME = "SwipRent";
const DEFAULT_DESCRIPTION =
  "SwipRent is the swipe first rental marketplace for Canada. Browse homes " +
  "for rent, like the ones you love and match with listers instantly.";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

/** Write the full head set for a page. Called by RouteSeo on every route
 *  change (defaults) and by data pages once their content loads (upgrades,
 *  e.g. the real listing title). */
export function applyMeta({ title, description, path, noindex = false }) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Swipe. Match. Move in.`;
  const desc = description || DEFAULT_DESCRIPTION;
  const url = `${SITE_URL}${path ?? window.location.pathname}`;

  document.title = fullTitle;
  upsertMeta("name", "description", desc);
  upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
  upsertCanonical(url);
  upsertMeta("property", "og:title", fullTitle);
  upsertMeta("property", "og:description", desc);
  upsertMeta("property", "og:url", url);
  upsertMeta("name", "twitter:title", fullTitle);
  upsertMeta("name", "twitter:description", desc);
}

/** For pages whose title depends on loaded data (listing detail, host
 *  profile). Pass null/undefined title while loading; the route default
 *  from RouteSeo stays up until real data arrives. */
export function usePageMeta(meta) {
  useEffect(() => {
    if (meta && meta.title) applyMeta(meta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.title, meta?.description, meta?.noindex]);
}
